import { standardizeString } from '@skop-app/lib-domain/utils'

import type {
  EditorOption,
  EditorOptionNode,
  EditorProperty,
  EditorValue,
} from './types'

const NON_CREATABLE_DYNAMIC_KEYS = new Set([
  'placeID',
  'projectID',
  'byUserID',
  'organizationID',
  'shapeID',
])

export const sanitizeCreateLabel = (value: string): string =>
  value.trim().replace(/\s+/g, ' ')

export const normalizeCreateLabel = (value: string): string =>
  standardizeString(sanitizeCreateLabel(value))

export const flattenEditorOptions = (
  options: EditorOptionNode[] | undefined,
): EditorOption[] => {
  if (!Array.isArray(options) || options.length === 0) return []

  const flattened: EditorOption[] = []
  const stack = [...options]

  while (stack.length > 0) {
    const option = stack.pop()
    if (!option) continue

    flattened.push(option)

    if (Array.isArray(option.options)) {
      for (const child of option.options) stack.push(child)
    }
  }

  return flattened
}

export const findExistingEditorOption = (
  options: EditorOptionNode[] | undefined,
  inputValue: string,
): EditorOption | undefined => {
  const normalizedInput = normalizeCreateLabel(inputValue)
  if (!normalizedInput) return undefined

  return flattenEditorOptions(options).find(
    (option) => normalizeCreateLabel(option.label) === normalizedInput,
  )
}

export const findMatchingReferenceOptions = (
  options: EditorOptionNode[],
  inputValue: string,
): EditorOption[] => {
  const label = normalizeCreateLabel(inputValue)
  if (!label) return []
  return flattenEditorOptions(options).filter(
    (option) =>
      !(option as EditorOptionNode).options &&
      option.isSelectable !== false &&
      (normalizeCreateLabel(option.label) === label ||
        (option.nameLabel && normalizeCreateLabel(option.nameLabel) === label)),
  )
}

export const canCreateInlineValue = (
  property: EditorProperty | undefined,
): boolean => {
  if (!property) return false
  if (property.type !== 'DYNAMIC') return true

  return !NON_CREATABLE_DYNAMIC_KEYS.has(property.key)
}

const toCommittedToken = (
  option: Pick<EditorOption, 'id' | 'value'>,
  fieldKey: string,
): string => {
  if (fieldKey === 'action') return option.value
  return option.id ?? option.value
}

export const toCommittedTokens = (
  value: EditorValue,
  fieldKey: string,
): string[] => {
  const items = Array.isArray(value) ? value : value != null ? [value] : []

  return items
    .map((item) => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object') {
        return toCommittedToken(item as EditorOption, fieldKey)
      }
      return undefined
    })
    .filter(
      (item): item is string => typeof item === 'string' && item.length > 0,
    )
}

export const mergeCommittedTokens = (params: {
  currentValue: EditorValue
  nextOption: Pick<EditorOption, 'id' | 'value'>
  fieldKey: string
  isMulti: boolean
}): string[] => {
  const { currentValue, nextOption, fieldKey, isMulti } = params
  const nextToken = toCommittedToken(nextOption, fieldKey)

  if (!isMulti) return [nextToken]

  const currentTokens = toCommittedTokens(currentValue, fieldKey)
  return Array.from(new Set([...currentTokens, nextToken]))
}
